import { Row, Col, Form, Spin, Tabs, Input, Button, message, Select } from 'antd';
import { useState, useEffect, useRef, useMemo } from 'react';
import Diagnostics, { DiagnosticCard } from '../../components/Diagnostics';
import ConclusionsModal from './ConclusionsModal';
import DiagnosticSelector from '../../components/DiagnosticSelector';
import { getCookie } from '../../../services/cookies';
import { consultationService } from '../../../services/medicalConsultation';
import useRecommendationsModal from '../../components/hooks/useRecommendationsModal';
import groupedExams from '../../../utils/groupedExams';
import ConsultationDiagnosticExamDrawer from './ConsultationDiagnosticExamDrawer';
import { EXAM_SPECIALTY_IDS } from '../constants/examSpecialties';
const { Option } = Select;

const { TabPane } = Tabs;

const buildDiagnosticTypificationConfig = (options) => {
  if (!options) return null;
  return {
    pathological: {
      label: 'Patológico',
      type: 'checkbox',
      tooltip: 'Habilitar diagnóstico en informe',
      options:
        options.pathological?.values.map(({ label, value }) => ({
          value,
          label,
        })) || [],
      defaultValue: options.pathological?.defaultValue ?? false,
    },
    occupational: {
      label: 'Tipo de diagnóstico',
      type: 'radio',
      tooltip: 'Tipo de diagnóstico médico',
      options:
        options.occupational?.values.map(({ key, value, shortLabel }) => ({
          value: key,
          label: value,
          shortLabel,
        })) || [],
      defaultValue: options.occupational?.defaultValue ?? 'other',
    },
    classification: {
      label: 'Clasificación',
      type: 'select',
      tooltip: 'Clasificación del diagnóstico',
      options:
        options.classification?.values.map(({ key, value }) => ({
          value: key,
          label: value,
        })) || [],
      defaultValue: options.classification?.defaultValue ?? 'presumptive',
    },
  };
};

export const DiagnosticsCardsHeader = ({ showDiagnosticTypification }) => (
  <Row gutter={[20, 20]} className="mt-20">
    {showDiagnosticTypification && (
      <Col lg={2}>
        <strong>Habilitar</strong>
      </Col>
    )}
    <Col lg={showDiagnosticTypification ? 2 : 4}>
      <strong>Código</strong>
    </Col>
    <Col lg={12}>
      <strong>Descripción</strong>
    </Col>
  </Row>
);

const ConsultationDiagnostics = ({
  medicalConsultationId,
  admissionPatientId,
  serviceId,
  serviceType,
  medicalFormType,
  t,
  countrySettings,
  countryCode,
  patientFullName,
  oldReport,
  oldReportCountry,
  examsList,
  patientData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagnosticTypificationOptions, setDiagnosticTypificationOptions] = useState(null);
  const [activeExamTab, setActiveExamTab] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [drawerExamId, setDrawerExamId] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const hasFetchedOptions = useRef(false);
  const ultrasoundSpecialtyId = EXAM_SPECIALTY_IDS[countryCode]?.ULTRASOUND;
  const isSpecialtyUltrasound = (specialtyId) => specialtyId === ultrasoundSpecialtyId;
  const interconsultationExamIds = new Set(
    (examsList || [])
      .filter((exam) => exam.form_type === 'interconsultation')
      .map((exam) => exam.admission_patient_exam_id),
  );
  const examDiagnostics = form.getFieldValue('examDiagnostics') || [];
  const specialtyDiagnostics = form.getFieldValue('specialtyDiagnostics') || [];
  const visibleExamDiagnostics = examDiagnostics.filter(exam => !isSpecialtyUltrasound(exam.examSpecialtyId) && !interconsultationExamIds.has(exam.examId));
  const visibleSpecialtyDiagnostics = specialtyDiagnostics.filter(specialty => !isSpecialtyUltrasound(specialty.examSpecialtyId));
  const specialtyIds = new Set(specialtyDiagnostics.map(specialty => String(specialty.examSpecialtyId)));
  const processedGroups = new Set();
  const groupedExamsList = (examsList || []).reduce((acc, exam) => {
    const groupName = groupedExams[exam.exam_type];
    const isSpecialtyWithDiagnostics = groupName && exam.exam_specialty_id && specialtyIds.has(String(exam.exam_specialty_id));
    if (isSpecialtyWithDiagnostics) {
      if (!processedGroups.has(exam.exam_type)) {
        processedGroups.add(exam.exam_type);
        acc.push({ ...exam, name: groupName, isGroup: true });
      }
    } else {
      acc.push(exam);
    }
    return acc;
  }, []).sort((a, b) => a.name.localeCompare(b.name));

  const drawerRecord = useMemo(() => {
    if (!drawerExamId) return null;
    const selectedExam = groupedExamsList?.find((exam) => exam.admission_patient_exam_id === drawerExamId);
    return {
      ...selectedExam,
      admission_patient_id: admissionPatientId,
      medical_consultation_id: medicalConsultationId,
      admission_patient_exam: drawerExamId,
      full_name: patientFullName,
      document_number: patientData?.document_number,
      document_type: patientData?.document_type,
      exam_name: selectedExam?.name,
      isGroup: selectedExam?.isGroup,
    };
  }, [drawerExamId, groupedExamsList, admissionPatientId, medicalConsultationId, patientFullName, patientData]);


  const roles = getCookie('roles');
  const canClassifyDiagnostics = roles.includes('medical_auditor');
  const showDiagnosticTypification = countrySettings.medicalConsultation.showDiagnosticsSection[countryCode] && canClassifyDiagnostics;

  const recommendatiosModal = useRecommendationsModal({
    t,
    service: {
      id: serviceId,
      type: serviceType,
      medicalFormType: medicalFormType,
    },
    patientFullName,
    countryCode,
    conclusionPosition: null,
    setConclusionText: () => { },
    oldReport,
    oldReportCountry,
    medicalForm: form,
    countrySettings,
  });
  useEffect(() => {
    if (hasFetchedOptions.current) return;

    const fetchOptions = async () => {
      try {
        const options = await consultationService.getDiagnosticTypificationOptions();
        const config = buildDiagnosticTypificationConfig(options);
        setDiagnosticTypificationOptions(config);
        hasFetchedOptions.current = true;
      } catch (error) {
        console.error('Error fetching diagnostic options:', error);
        message.error({
          content: 'Error al cargar opciones de diagnóstico',
          style: { marginTop: '45vh' },
        });
      }
    };

    fetchOptions();
  }, []);

  const fetchDiagnosticsData = async (options = {}) => {
    const { preserveUnsaved = false } = options;
    if (!diagnosticTypificationOptions || !medicalConsultationId) return;

    try {
      setLoading(true);
      const response = await consultationService.getDiagnosticsForConsultation({
        medicalConsultationId,
        admissionPatientId,
        serviceId,
        serviceType,
      });

      const normalize = (arr) =>
        arr.map((exam) => {
          const normalizedDiagnostics = exam.diagnostics.map((diagnostic) => ({
            id: diagnostic.id,
            code: diagnostic.code,
            description: diagnostic.description,
            diagnostic_id: diagnostic.diagnostic_id,
            diagnosticable_id: diagnostic.diagnosticable_id,
            diagnosticable_type: diagnostic.diagnosticable_type,
            indication: diagnostic.indications,
            suggestion: diagnostic.suggestions,
            pathological: diagnostic.pathological ?? diagnosticTypificationOptions?.pathological?.defaultValue,
            classification: diagnostic.classification ?? diagnosticTypificationOptions?.classification?.defaultValue,
            occupational: diagnostic.occupational ?? diagnosticTypificationOptions?.occupational?.defaultValue,
            expertFieldId: diagnostic.expertFieldId,
            expertFieldName: diagnostic.expertFieldName,
          }));
          const examLevelDiagnostics = normalizedDiagnostics.filter((d) => d.diagnosticable_type === 'AdmissionPatientExam');

          const expertFieldDiagnostics = Object.values(
            normalizedDiagnostics
              .filter((diagnostic) => diagnostic.diagnosticable_type === 'AdmissionExpertFieldResult')
              .reduce((groups, diagnostic) => {
                const key = diagnostic.expertFieldName || 'Otros campos';
                if (!groups[key]) groups[key] = { fieldName: key, diagnostics: [] };
                groups[key].diagnostics.push(diagnostic);
                return groups;
              }, {}),
          );

          return {
            ...exam,
            examLevelDiagnostics,
            expertFieldDiagnostics,
          };
        });

      const normalizedExamDiagnostics = normalize(response.examDiagnostics || []);

      const eligibleExamsFromList = (examsList || []).filter(listExam => {
        const alreadyIncluded = normalizedExamDiagnostics.some(normalized => normalized.examId === listExam.admission_patient_exam_id);
        return !alreadyIncluded
          && listExam.form_type !== 'interconsultation'
          && (listExam.need_expert_sign || listExam.allow_diagnostics);
      });
      const allExamDiagnostics = [
        ...normalizedExamDiagnostics,
        ...eligibleExamsFromList.map(listExam => ({
          examId: listExam.admission_patient_exam_id,
          examName: listExam.name,
          examSpecialtyId: listExam.exam_specialty_id,
          examLevelDiagnostics: [],
          expertFieldDiagnostics: [],
        })),
      ];

      const normalizedSpecialtyDiagnostics = (response.specialtyDiagnostics || []).map(specialty => {
        const normalizedDiagnostics = (specialty.diagnostics || []).map((diagnostic) => ({
          id: diagnostic.id,
          code: diagnostic.code,
          description: diagnostic.description,
          diagnostic_id: diagnostic.diagnostic_id,
          diagnosticable_id: diagnostic.diagnosticable_id,
          diagnosticable_type: diagnostic.diagnosticable_type,
          indication: diagnostic.indications,
          suggestion: diagnostic.suggestions,
          pathological: diagnostic.pathological ?? diagnosticTypificationOptions?.pathological?.defaultValue,
          classification: diagnostic.classification ?? diagnosticTypificationOptions?.classification?.defaultValue,
          occupational: diagnostic.occupational ?? diagnosticTypificationOptions?.occupational?.defaultValue,
        }));

        return {
          examSpecialtyId: specialty.examSpecialtyId || specialty.examId,
          specialtyName: specialty.specialtyName || specialty.examName,
          diagnostics: normalizedDiagnostics,
        };
      });

      const currentExamDiagnostics = form.getFieldValue('examDiagnostics') || [];
      const currentSpecialtyDiagnostics = form.getFieldValue('specialtyDiagnostics') || [];
      const savedExamDetails = groupedExamsList?.find((exam) => exam.admission_patient_exam_id === drawerExamId);
      const isSavingGroup = !!savedExamDetails?.isGroup;
      const savedExamId = drawerExamId;

      form.setFieldsValue({
        medicalConsultationDiagnostics: preserveUnsaved && form.getFieldValue('medicalConsultationDiagnostics') != null
          ? form.getFieldValue('medicalConsultationDiagnostics')
          : (response.medicalConsultationDiagnostics || []).map((diagnostic) => ({
            id: diagnostic.id,
            code: diagnostic.code,
            description: diagnostic.description,
            diagnostic_id: diagnostic.diagnostic_id,
            diagnosticable_id: diagnostic.diagnosticable_id,
            diagnosticable_type: diagnostic.diagnosticable_type,
            indication: diagnostic.indications,
            suggestion: diagnostic.suggestions,
            pathological: diagnostic.pathological ?? diagnosticTypificationOptions?.pathological?.defaultValue,
            classification: diagnostic.classification ?? diagnosticTypificationOptions?.classification?.defaultValue,
            occupational: diagnostic.occupational ?? diagnosticTypificationOptions?.occupational?.defaultValue,
          })),
        examDiagnostics: allExamDiagnostics.map((updatedExamData) => {
          const unsavedExamData = currentExamDiagnostics.find((exam) => String(exam.examId) === String(updatedExamData.examId));
          const shouldRefreshExamTab = !isSavingGroup && String(updatedExamData.examId) === String(savedExamId);

          if (preserveUnsaved && unsavedExamData && !shouldRefreshExamTab) {
            return unsavedExamData;
          }
          return {
            examId: updatedExamData.examId,
            examName: updatedExamData.examName,
            examSpecialtyId: updatedExamData.examSpecialtyId,
            diagnostics: updatedExamData.examLevelDiagnostics || [],
            expertFieldDiagnostics: updatedExamData.expertFieldDiagnostics || [],
          };
        }),
        specialtyDiagnostics: normalizedSpecialtyDiagnostics.map((updatedSpecialtyData) => {
          const unsavedSpecialtyData = currentSpecialtyDiagnostics.find(
            (specialty) => String(specialty.examSpecialtyId) === String(updatedSpecialtyData.examSpecialtyId),
          );
          const shouldRefreshSpecialtyTab = isSavingGroup && (
            String(updatedSpecialtyData.examSpecialtyId) === String(savedExamDetails?.exam_specialty_id)
          );
          if (preserveUnsaved && unsavedSpecialtyData && !shouldRefreshSpecialtyTab) {
            return unsavedSpecialtyData;
          }
          return updatedSpecialtyData;
        }),
      });

      const visibleAllExamDiagnostics = allExamDiagnostics.filter((exam) => !isSpecialtyUltrasound(exam.examSpecialtyId) && !interconsultationExamIds.has(exam.examId));
      const visibleNormalizedSpecialtyDiagnostics = normalizedSpecialtyDiagnostics.filter((specialty) => !isSpecialtyUltrasound(specialty.examSpecialtyId));

      if (visibleAllExamDiagnostics.length > 0 && !activeExamTab) {
        setActiveExamTab(`exam_${visibleAllExamDiagnostics[0]?.examId}`);
      } else if (visibleNormalizedSpecialtyDiagnostics.length > 0 && !activeExamTab) {
        setActiveExamTab(`specialty_${visibleNormalizedSpecialtyDiagnostics[0]?.examSpecialtyId}`);
      }
    } catch (error) {
      console.error('Error fetching diagnostics:', error);
      message.error({
        content: 'Error al cargar diagnósticos',
        style: { marginTop: '45vh' },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnosticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalConsultationId, diagnosticTypificationOptions, admissionPatientId, serviceId, serviceType, examsList]);

  useEffect(() => {
    if (activeExamTab === null) {
      if (visibleExamDiagnostics.length > 0) {
        setActiveExamTab(`exam_${visibleExamDiagnostics[0]?.examId}`);
      } else if (visibleSpecialtyDiagnostics.length > 0) {
        setActiveExamTab(`specialty_${visibleSpecialtyDiagnostics[0]?.examSpecialtyId}`);
      }
    }
  }, [activeExamTab, form]);

  const onFinish = async (values) => {
    try {

      setSaving(true);
      const cleanDiagnosticsGroup = (groups = []) => {
        return groups
          .filter(Boolean)
          .filter((group) => !isSpecialtyUltrasound(group.examSpecialtyId))
          .map((group) => {
            const { examId, examName } = group;
            const cleanExamDiagnostics = (group.diagnostics || []).filter((diagnostic) => diagnostic?.id || diagnostic?.diagnostic_id);
            const cleanExpertFieldDiagnostics = (group.expertFieldDiagnostics || [])
              .map((expertField) => ({
                ...expertField,
                diagnostics: (expertField.diagnostics || []).filter((diagnostic) => diagnostic?.id || diagnostic?.diagnostic_id),
              }))
              .filter((expertField) => expertField?.diagnostics?.length > 0);
            return {
              examId,
              examName,
              diagnostics: cleanExamDiagnostics,
              expertFieldDiagnostics: cleanExpertFieldDiagnostics,
            };
          });
      };

      const cleanSpecialtyDiagnostics = (groups = []) => {
        return groups
          .filter(Boolean)
          .filter((group) => !isSpecialtyUltrasound(group.examSpecialtyId))
          .map((group) => {
            const { examSpecialtyId, specialtyName } = group;
            const cleanDiagnostics = (group.diagnostics || []).filter((diagnostic) => diagnostic?.id || diagnostic?.diagnostic_id);
            return {
              examSpecialtyId,
              specialtyName,
              diagnostics: cleanDiagnostics,
            };
          });
      };

      await consultationService.saveDiagnostics({
        medicalConsultationId: medicalConsultationId,
        medicalConsultationDiagnostics: values['medicalConsultationDiagnostics'] || [],
        examDiagnostics: cleanDiagnosticsGroup(values['examDiagnostics']) || [],
        specialtyDiagnostics: cleanSpecialtyDiagnostics(values['specialtyDiagnostics']) || [],
      });

      message.success({
        content: 'Diagnósticos guardados exitosamente',
        style: { marginTop: '45vh' },
      });

      await fetchDiagnosticsData();
    } catch (error) {
      console.error('Error saving diagnostics:', error);
      message.error({
        content: 'Error al guardar diagnósticos',
        style: { marginTop: '45vh' },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Spin spinning={loading} className="cmt-spin">
        <Form form={form} onFinish={onFinish} className="consultation-diagnostics-form">
          <div className="diagnostics-search-row">
            <Form.Item label="Exámenes" className="diagnostics-search-field">
              <div className="search-input-container">
                <i className="icon-system-search" />
                <Select
                  showSearch
                  placeholder="Buscar o seleccionar examen..."
                  className="cmt-select diagnostics-search-select"
                  style={{ width: 300 }}
                  value={selectedExamId}
                  onChange={(value) => setSelectedExamId(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {groupedExamsList?.map((exam) => (
                    <Option key={exam.admission_patient_exam_id} value={exam.admission_patient_exam_id}>
                      {exam.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Form.Item>
            <Button
              className="cmt-button secondary search-button-diagnostics"
              onClick={() => {
                setDrawerExamId(selectedExamId);
                setDrawerVisible(true);
              }}
              disabled={!selectedExamId}
            >
              Buscar
            </Button>
          </div>
          <div>
            <Row className="mt-40">
              <Col lg={24} xs={24}>
                <h2 className="mb-10">Diagnósticos de la atención médica</h2>
              </Col>
              <Col lg={24} xs={24}>
                <p className="bold">CIE 10</p>
              </Col>
              <Col lg={24} xs={24}>
                <Diagnostics
                  form={form}
                  recommendatiosModal={recommendatiosModal}
                  showDiagnosticTypification={showDiagnosticTypification}
                  diagnosticTypificationOptions={diagnosticTypificationOptions}
                  inputData={{ id: 'medicalConsultationDiagnostics' }}
                />
              </Col>
            </Row>

            <Row className="mt-40">
              <Col lg={24} xs={24}>
                <h2 className="mb-10">Diagnósticos de exámenes especialistas</h2>
              </Col>
              <Col lg={24} xs={24}>
                <Tabs activeKey={activeExamTab} onChange={setActiveExamTab} destroyInactiveTabPane={false}>
                  {visibleExamDiagnostics.map((exam, examIdx) => {
                    const examType = 'examDiagnostics';
                    const examIndexInList = examDiagnostics.findIndex(examInList => examInList.examId === exam.examId);

                    return (
                      <TabPane tab={<strong>{exam.examName}</strong>} key={`exam_${exam.examId}`} forceRender={true}>
                        <div className="diagnostics-search-row mb-20">
                          <Button
                            className="cmt-button secondary outline view-exam-btn"
                            onClick={() => {
                              setDrawerExamId(exam.examId);
                              setDrawerVisible(true);
                            }}
                          >
                            Ver examen
                          </Button>
                        </div>
                        <Form.Item name={[examType, examIndexInList, 'examId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[examType, examIndexInList, 'examName']} hidden>
                          <Input />
                        </Form.Item>
                        <Row className="mb-20">
                          <Col lg={24}>
                            <DiagnosticSelector
                              form={form}
                              diagnosticField={[examType, examIndexInList, 'diagnostics']}
                              placeholder={`Buscar diagnóstico para ${exam.examName}...`}
                              showDiagnosticTypification={showDiagnosticTypification}
                              diagnosticTypificationOptions={diagnosticTypificationOptions}
                            />
                          </Col>
                        </Row>
                        <Form.List name={[examType, examIndexInList, 'diagnostics']}>
                          {(fields, { remove }) => (
                            <>
                              {fields.length > 0 && <DiagnosticsCardsHeader showDiagnosticTypification={showDiagnosticTypification} />}
                              {fields.map((field, diagnosticIndex) => (
                                <DiagnosticCard
                                  key={field.key}
                                  field={field}
                                  index={diagnosticIndex}
                                  onRemove={remove}
                                  recommendatiosModal={recommendatiosModal}
                                  diagnosticField={[examType, examIndexInList, 'diagnostics']}
                                  showDiagnosticTypification={showDiagnosticTypification}
                                  diagnosticTypificationOptions={diagnosticTypificationOptions}
                                  t={t}
                                  form={form}
                                />
                              ))}
                            </>
                          )}
                        </Form.List>

                        {exam.expertFieldDiagnostics?.length > 0 && (
                          <Form.List name={[examType, examIndexInList, 'expertFieldDiagnostics']}>
                            {(groups) => (
                              <>
                                {groups.map((group, groupIdx) => {
                                  const groupValues = form.getFieldValue([examType, examIndexInList, 'expertFieldDiagnostics', groupIdx]) || {};
                                  const fieldName = groupValues.fieldName || 'Otros campos';
                                  const diagnostics = groupValues.diagnostics || [];

                                  return (
                                    <div key={group.key} className="mt-30">
                                      <h3 className="mb-10">{fieldName}</h3>

                                      {diagnostics.length > 0 && <DiagnosticsCardsHeader showDiagnosticTypification={showDiagnosticTypification} />}

                                      <Form.List name={[groupIdx, 'diagnostics']}>
                                        {(fields, { remove }) => (
                                          <>
                                            {fields.map((field, diagnosticIndex) => (
                                              <DiagnosticCard
                                                key={field.key}
                                                field={field}
                                                index={diagnosticIndex}
                                                onRemove={remove}
                                                recommendatiosModal={recommendatiosModal}
                                                diagnosticField={[examType, examIndexInList, 'expertFieldDiagnostics', groupIdx, 'diagnostics']}
                                                showDiagnosticTypification={showDiagnosticTypification}
                                                diagnosticTypificationOptions={diagnosticTypificationOptions}
                                                t={t}
                                                form={form}
                                              />
                                            ))}
                                          </>
                                        )}
                                      </Form.List>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </Form.List>
                        )}
                      </TabPane>
                    );
                  })}

                  {visibleSpecialtyDiagnostics.map((specialty, specialtyIndex) => {
                    const examType = 'specialtyDiagnostics';

                    return (
                      <TabPane tab={<strong>{specialty.specialtyName}</strong>} key={`specialty_${specialty.examSpecialtyId}`} forceRender={true}>
                        {(() => {
                          const matchingGroup = groupedExamsList?.find(
                            (exam) =>
                              exam.isGroup && (
                                String(exam.exam_specialty_id) === String(specialty.examSpecialtyId)
                              )
                          );

                          return matchingGroup ? (
                            <div className="diagnostics-search-row mb-20">
                              <Button
                                className="cmt-button secondary outline view-exam-btn"
                                onClick={() => {
                                  setDrawerExamId(matchingGroup.admission_patient_exam_id);
                                  setDrawerVisible(true);
                                }}
                              >
                                Ver exámenes
                              </Button>
                            </div>
                          ) : null;
                        })()}
                        <Form.Item name={[examType, specialtyIndex, 'examSpecialtyId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[examType, specialtyIndex, 'specialtyName']} hidden>
                          <Input />
                        </Form.Item>
                        <Row className="mb-20">
                          <Col lg={24}>
                            <DiagnosticSelector
                              form={form}
                              diagnosticField={[examType, specialtyIndex, 'diagnostics']}
                              placeholder={`Buscar diagnóstico para ${specialty.specialtyName}...`}
                              showDiagnosticTypification={showDiagnosticTypification}
                              diagnosticTypificationOptions={diagnosticTypificationOptions}
                            />
                          </Col>
                        </Row>
                        <Form.List name={[examType, specialtyIndex, 'diagnostics']}>
                          {(fields, { remove }) => (
                            <>
                              {fields.length > 0 && <DiagnosticsCardsHeader showDiagnosticTypification={showDiagnosticTypification} />}
                              {fields.map((field, diagnosticIndex) => (
                                <DiagnosticCard
                                  key={field.key}
                                  field={field}
                                  index={diagnosticIndex}
                                  onRemove={remove}
                                  recommendatiosModal={recommendatiosModal}
                                  diagnosticField={[examType, specialtyIndex, 'diagnostics']}
                                  showDiagnosticTypification={showDiagnosticTypification}
                                  diagnosticTypificationOptions={diagnosticTypificationOptions}
                                  t={t}
                                  form={form}
                                />
                              ))}
                            </>
                          )}
                        </Form.List>
                      </TabPane>
                    );
                  })}
                </Tabs>
              </Col>
            </Row>

            <Row className="mt-40" justify="end">
              <Col>
                <Button type="primary" htmlType="submit" loading={saving} className="cmt-button primary" size="large">
                  Guardar diagnósticos
                </Button>
              </Col>
            </Row>
          </div>
          {drawerExamId && (
            <ConsultationDiagnosticExamDrawer
              open={drawerVisible}
              onClose={() => setDrawerVisible(false)}
              record={drawerRecord}
              onExamSaved={() => {
                fetchDiagnosticsData({ preserveUnsaved: true });
              }}
              form={form}
              examDiagnostics={examDiagnostics}
              specialtyDiagnostics={specialtyDiagnostics}
              showDiagnosticTypification={showDiagnosticTypification}
              diagnosticTypificationOptions={diagnosticTypificationOptions}
              recommendatiosModal={recommendatiosModal}
            />
          )}
        </Form>
      </Spin>
      <ConclusionsModal recommendatiosModal={recommendatiosModal} />
    </>
  );
};

export default ConsultationDiagnostics;
